<div
  [formGroup]="form"
  [class.content]="!disableCropFieldSelector()"
  [class.content-embedded]="disableCropFieldSelector()"
>
  <div
    class="flex flex--row full-width flex--align-center header"
    [class.flex--justify-space-between]="!disableCropFieldSelector()"
    [class.flex--justify-end]="disableCropFieldSelector()"
  >
    @if (!disableCropFieldSelector()) {
      <div class="flex__child">
        <div class="custom-selector">
          <label class="placeholder" id="cropFieldSelectLabel" for="cropFieldSelect">{{
            'blightV2.shared.selectCropField' | translate
          }}</label>
          <div class="crop-field-select-sizer">
            <span class="crop-field-select-sizer__text" aria-hidden="true">{{ longestOptionText() }}</span>
            <mat-select
              formControlName="cropField"
              panelClass="disease-crop-field-select-panel"
              id="cropFieldSelect"
              aria-labelledby="cropFieldSelectLabel"
            >
              @for (cropField of cropFields(); track cropField.cropFieldId) {
                <mat-option [value]="cropField.cropFieldId">{{ cropField.cropFieldName }}</mat-option>
              }
            </mat-select>
          </div>
        </div>
        @if (selectedCropFieldAdvice()?.cropVarietyName) {
          <span class="crop-variety-label"
            ><strong>{{ 'BFP.UI.Variety' | translate }}:</strong> {{ selectedCropFieldAdvice()?.cropVarietyName }}</span
          >
        }

        <button
          mat-flat-button
          color="accent"
          class="action-button"
          [matMenuTriggerFor]="virusMonitorMenu.menu"
          (menuOpened)="onActionMenuOpened('button')"
        >
          <mat-icon class="table-toolbar-button-icon action-button-icon">add_task</mat-icon>
          <span class="flex--align-center table-toolbar-button-span">
            {{ 'shared.diseaseTable.actions' | translate }}
            <mat-icon>keyboard_arrow_down</mat-icon>
          </span>
        </button>
      </div>
    }

    <div class="flex">
      <div class="flex--row">
        <app-recalculate-button
          (recalculate)="onRecalculate()"
          [disabled]="isRecalculationDisabled()"
          [disabledTooltip]="isCropYearReadOnly() ? ('UI.CurrentYearOnlyMutationMessage' | translate) : ''"
          [isLoading]="store.isVirusMonitorCropFieldsSyncing()"
          [label]="'shared.diseaseTable.recalculateAdviceInsight' | translate"
        />
      </div>
      <div class="flex--row flex--align-center table-toolbar-button-group">
        <app-date-selector
          [minimalDate]="minimalDate()"
          [maximalDate]="maximalDate()"
          [selectedDates]="selectedDates()"
          [cropYear]="store.cropYear()"
          (selectedDatesChanges)="onSelectedDatesChanged($event)"
        />
      </div>
    </div>
  </div>

  @if (selectedCropField(); as cropField) {
    <div class="toolbar-container">
      <app-virus-monitor-insight-toolbar
        [selectedCropField]="cropField"
        [isParticipatingToBem]="store.selectedCropFieldIsParticipatingToBem()"
        [isReadOnly]="isReadOnly()"
        (plantDateChanged)="onPlantDateChanged($event)"
        (emergenceDateChanged)="onEmergenceDateChanged($event)"
        (harvestDateChanged)="onHarvestDateChanged($event)"
        (deadFoliageDateChanged)="onDeadFoliageDateChanged($event)"
        (sprayThresholdChanged)="onSprayThresholdChanged($event)"
      />
    </div>
  }

  @if (store.selectedCropFieldId()) {
    <app-disease-insight-chart
      [selectedRange]="selectedDates()"
      [chartDays]="chartDays()"
      [advice]="adviceForChart()"
      [betweenChartAndTableTemplate]="betweenChartAndTableTemplate"
      [riskTableRowsTemplate]="riskTableRows"
      (actionTriggered)="onActionTriggered($event)"
      (seriesToggled)="onGraphSeriesToggled($event)"
    />
  }
</div>

<span
  #menuTrigger="matMenuTrigger"
  [matMenuTriggerFor]="virusMonitorMenu.menu"
  (menuOpened)="onActionMenuOpened('chart')"
  style="position: fixed; visibility: hidden; pointer-events: none"
></span>
<app-virus-monitor-action-menu
  #virusMonitorMenu
  [advices]="selectedAdvice()"
  [actionDate]="actionDate()"
  context="insights"
/>

<ng-template #betweenChartAndTableTemplate>
  @if (selectedCropField(); as cropField) {
    <app-virus-monitor-protection-type-selector
      [availableProtectionTypes]="cropField.availableProtectionTypes"
      [disabled]="isReadOnly()"
      [selectedProtectionTypes]="cropField.selectedProtectionTypes"
      [warningProtectionTypes]="warningProtectionTypes()"
      [protectionTypeDetails]="protectionTypeDetails()"
      (protectionTypesChanged)="onProtectionTypesChanged($event)"
    />
  }
</ng-template>

<ng-template #riskTableRows let-days let-chart="chart">
  <tr class="details-row">
    <td class="no-borders">
      <div class="hover-text">
        <img alt="" src="assets/icons/oil_percent.svg" />
        <span class="tooltip-text right">{{ 'virusMonitor.riskChart.oilProtection' | translate }}</span>
      </div>
    </td>
    @for (day of days; track day.date) {
      <td
        class="risk-cell"
        [style]="getProtectionLevelStyling(day.oilProtectionPercent)"
        (mouseenter)="chart.showTooltip($event, tooltipFormatter(day, 'oilProtection'))"
        (mouseleave)="chart.hideTooltip()"
      ></td>
    }
  </tr>

  <tr class="details-row">
    <td class="no-borders">
      <div class="hover-text">
        <img alt="" src="assets/icons/pyrethroids_percent.svg" />
        <span class="tooltip-text right">{{ 'virusMonitor.riskChart.pyrethroidProtection' | translate }}</span>
      </div>
    </td>
    @for (day of days; track day.date) {
      <td
        class="risk-cell"
        [style]="getProtectionLevelStyling(day.pyrethroidProtectionPercent)"
        (mouseenter)="chart.showTooltip($event, tooltipFormatter(day, 'pyrethroidProtection'))"
        (mouseleave)="chart.hideTooltip()"
      ></td>
    }
  </tr>

  <tr class="details-row">
    <td class="no-borders">
      <div class="hover-text">
        <img alt="" src="assets/icons/systemic_percent.svg" />
        <span class="tooltip-text right">{{ 'virusMonitor.riskChart.systemicProtection' | translate }}</span>
      </div>
    </td>
    @for (day of days; track day.date) {
      <td
        class="risk-cell"
        [style]="getProtectionLevelStyling(day.systemicProtectionPercent)"
        (mouseenter)="chart.showTooltip($event, tooltipFormatter(day, 'systemicProtection'))"
        (mouseleave)="chart.hideTooltip()"
      ></td>
    }
  </tr>

  <tr class="details-row">
    <td class="no-borders">
      <div class="hover-text">
        <img class="uv-icon" alt="" src="assets/icons/sunny.svg" />
        <span class="tooltip-text right">{{ 'virusMonitor.riskChart.uvIndex' | translate }}</span>
      </div>
    </td>
    @for (day of days; track day.date) {
      <td class="risk-cell">
        <div
          class="uv-index-circle"
          [style]="getUvIndexStyling(day.uvIndex)"
          (mouseenter)="chart.showTooltip($event, tooltipFormatter(day, 'uvIndex'))"
          (mouseleave)="chart.hideTooltip()"
        >
          {{ day.uvIndex | number: '1.0-0' }}
        </div>
      </td>
    }
  </tr>
</ng-template>
